import * as React from 'react';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  ClickAwayListener,
  Container,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import backgroundImg from '../../images/pexels-gromakova-92378617-9146943.jpg';
import {
  getCurrentPosition,
  getPostcodeAutocomplete,
  isPostcode,
  PostcodeSuggestion,
} from '../../utils/geocodingUtils';

export default function FindBarbersSection() {
  const navigate = useNavigate();
  const [postcode, setPostcode] = useState('');
  const [locating, setLocating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<PostcodeSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const autocompleteDebounceRef = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);
  const latestQueryRef = useRef<string>(''); // Track the latest input value

  const handleSearch = (queryOverride?: string) => {
    const trimmed = (queryOverride ?? postcode).trim();
    navigate(
      trimmed
        ? `/find-barbers?postcode=${encodeURIComponent(trimmed)}`
        : '/find-barbers'
    );
  };

  const handlePostcodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPostcode(value);
    latestQueryRef.current = value; // Track the latest input

    if (autocompleteDebounceRef.current) {
      clearTimeout(autocompleteDebounceRef.current);
    }

    if (value.trim().length >= 2) {
      setLoading(true);
      autocompleteDebounceRef.current = setTimeout(async () => {
        const results = await getPostcodeAutocomplete(value);

        // Ensure the request corresponds to the latest input value before state updates
        if (latestQueryRef.current.trim() === value.trim()) {
          setSuggestions(results);
          setShowSuggestions(results.length > 0);
          setLoading(false);
        }
      }, 300);
    } else {
      setLoading(false);
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelectSuggestion = (suggestion: PostcodeSuggestion) => {
    setPostcode(suggestion.postcode);
    setSuggestions([]);
    setShowSuggestions(false);
    handleSearch(suggestion.postcode);
  };

  // Resolves the user's browser location and navigates to the results page,
  // which geocodes the coordinates and searches barbers by location.
  const handleUseMyLocation = async () => {
    setLocating(true);
    try {
      const coordinates = await getCurrentPosition();
      navigate(`/find-barbers?lat=${coordinates.lat}&lng=${coordinates.lng}`);
    } catch (err) {
      console.error('Could not determine current location: ', err);
      navigate('/find-barbers');
    } finally {
      setLocating(false);
    }
  };

  return (
    <Box
      id="find-a-barber"
      sx={{
        backgroundImage: `linear-gradient(rgba(8,9,12,0.82), rgba(8,9,12,0.9)), url(${backgroundImg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        py: 10,
        px: 2,
        color: '#fff',
        textAlign: 'center',
        mt: 6,
      }}
    >
      <Container maxWidth="sm">
        <Typography
          variant="h4"
          sx={{ fontFamily: 'GFS Didot', fontWeight: 'bold', mb: 2 }}
        >
          Find a Barber Near You
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
          }}
        >
          <ClickAwayListener onClickAway={() => setShowSuggestions(false)}>
            <Box sx={{ position: 'relative', width: '100%' }}>
              <TextField
                fullWidth
                placeholder="Enter postcode, city or location"
                value={postcode}
                onChange={handlePostcodeChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSearch();
                }}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.06)',
                  borderRadius: 2,
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255,255,255,0.45)',
                    },
                    '&.Mui-focused fieldset': { borderColor: '#dd8709' },
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255,255,255,0.6)',
                    opacity: 1,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Tooltip title="Use my current location">
                        <span>
                          <IconButton
                            size="small"
                            onClick={handleUseMyLocation}
                            disabled={locating}
                            aria-label="Use my current location"
                          >
                            {locating ? (
                              <CircularProgress
                                size={18}
                                sx={{ color: '#dd8709' }}
                              />
                            ) : (
                              <MyLocationIcon sx={{ color: '#dd8709' }} />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </InputAdornment>
                  ),
                  endAdornment: loading ? (
                    <InputAdornment position="end">
                      <CircularProgress size={18} sx={{ color: '#dd8709' }} />
                    </InputAdornment>
                  ) : null,
                }}
              />
              {showSuggestions && suggestions.length > 0 && (
                <Paper
                  sx={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 10,
                    mt: 0.5,
                    bgcolor: '#1b1c22',
                    border: '1px solid rgba(255,255,255,0.08)',
                    textAlign: 'left',
                  }}
                >
                  <List dense disablePadding>
                    {suggestions.map((suggestion) => (
                      <ListItemButton
                        key={suggestion.placeId}
                        onClick={() => handleSelectSuggestion(suggestion)}
                      >
                        <ListItemText
                          primary={suggestion.text}
                          sx={{ color: '#fff' }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Paper>
              )}
            </Box>
          </ClickAwayListener>
          <Button
            variant="contained"
            onClick={() => handleSearch()}
            startIcon={<SearchIcon />}
            sx={{
              px: 4,
              minWidth: 160,
              bgcolor: '#dd8709',
              fontWeight: 'bold',
              fontSize: 10,
              borderRadius: 2,
              '&:hover': { bgcolor: '#b86e08' },
            }}
          >
            Find Barbers
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
